import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  BookOpen,
  Building2,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  FileText,
  FolderTree,
  Globe,
  Landmark,
  LayoutGrid,
  Megaphone,
  Monitor,
  LayoutDashboard,
  LifeBuoy,
  LogIn,
  LogOut,
  Mail,
  Map,
  Menu,
  Package,
  PenLine,
  Percent,
  Plug,
  Receipt,
  Settings,
  Shield,
  ShoppingCart,
  SlidersHorizontal,
  TabletSmartphone,
  Tags,
  Truck,
  Users,
  Wallet,
  Zap,
} from "lucide-react";

export type NavIconName =
  | "LayoutDashboard"
  | "LifeBuoy"
  | "Map"
  | "CalendarDays"
  | "Users"
  | "Menu"
  | "LogIn"
  | "LogOut"
  | "Mail"
  | "Receipt"
  | "ShoppingCart"
  | "BookOpen"
  | "Wallet"
  | "ClipboardCheck"
  | "BarChart3"
  | "Shield"
  | "Settings"
  | "Tags"
  | "Truck"
  | "Package"
  | "ClipboardList"
  | "FileText"
  | "FolderTree"
  | "Globe"
  | "Landmark"
  | "LayoutGrid"
  | "Megaphone"
  | "PenLine"
  | "Monitor"
  | "Building2"
  | "Percent"
  | "Plug"
  | "SlidersHorizontal"
  | "TabletSmartphone"
  | "Zap";

export const NAV_ICONS: Record<NavIconName, LucideIcon> = {
  LayoutDashboard,
  LifeBuoy,
  Map,
  CalendarDays,
  Users,
  Menu,
  LogIn,
  LogOut,
  Mail,
  Receipt,
  ShoppingCart,
  BookOpen,
  Wallet,
  ClipboardCheck,
  BarChart3,
  Shield,
  Settings,
  Tags,
  Truck,
  Package,
  PenLine,
  ClipboardList,
  FileText,
  FolderTree,
  Globe,
  Landmark,
  LayoutGrid,
  Megaphone,
  Monitor,
  Building2,
  Percent,
  Plug,
  SlidersHorizontal,
  TabletSmartphone,
  Zap,
};

export interface NavItem {
  key: string;
  label: string;
  href: string;
  icon?: NavIconName;
  description?: string;
  /** Zusätzliche Suchbegriffe (Alltagssprache), z. B. „Anzahlung“ oder „Logo“ */
  keywords?: string[];
  /** Schlüssel für Badge-Zähler aus NavProvider */
  badgeKey?:
    | "arrivalsToday"
    | "departuresToday"
    | "inquiriesOpen"
    | "chargeRulesRequiredOpen"
    | "ticketsOpen";
}

export interface NavGroup {
  key: string;
  label: string;
  items: NavItem[];
}

/** Mobile Bottom Navigation – Rezeptions-Kern (Platzplan/Buchungen über Schnellzugriff & „Mehr“) */
export const BOTTOM_NAV: NavItem[] = [
  { key: "home", label: "Start", href: "/", icon: "LayoutDashboard", description: "Rezeptions-Dashboard" },
  {
    key: "arrivals",
    label: "Anreisen",
    href: "/arrivals",
    icon: "LogIn",
    badgeKey: "arrivalsToday",
    description: "Heutige Anreisen",
  },
  {
    key: "departures",
    label: "Abreisen",
    href: "/departures",
    icon: "LogOut",
    badgeKey: "departuresToday",
    description: "Heutige Abreisen",
  },
  {
    key: "mail",
    label: "E-Mail",
    href: "/mail",
    icon: "Mail",
    description: "Posteingang, E-Mail-Vorlagen und Rundmails",
  },
  {
    key: "bookings",
    label: "Buchungen",
    href: "/bookings?status=requested",
    icon: "CalendarDays",
    badgeKey: "inquiriesOpen",
    description: "Reservierungen & offene Anfragen",
  },
  {
    key: "pos",
    label: "Kasse",
    href: "/pos",
    icon: "ShoppingCart",
    description: "Schnellverkauf ohne Buchung",
  },
  { key: "more", label: "Mehr", href: "#more", icon: "Menu" },
];

/** Bottom-Nav ohne „Mehr“-Platzhalter (stabile Referenz für Hooks) */
export const BOTTOM_NAV_PRIMARY: NavItem[] = BOTTOM_NAV.filter((i) => i.href !== "#more");

/** Desktop-Sidebar: häufig genutzte Bereiche (Rezeption) */
export const SIDEBAR_PRIMARY: NavItem[] = [
  { key: "home", label: "Dashboard", href: "/", icon: "LayoutDashboard", description: "Rezeptionsübersicht" },
  {
    key: "arrivals",
    label: "Anreisen",
    href: "/arrivals",
    icon: "LogIn",
    badgeKey: "arrivalsToday",
    description: "Heutige Anreisen",
  },
  {
    key: "departures",
    label: "Abreisen",
    href: "/departures",
    icon: "LogOut",
    badgeKey: "departuresToday",
    description: "Heutige Abreisen",
  },
  {
    key: "on-site",
    label: "Gäste am Platz",
    href: "/gaeste-am-platz",
    icon: "Users",
    description: "Eingecheckte Gäste vor Ort",
  },
  {
    key: "electricity",
    label: "Strom",
    href: "/strom",
    icon: "Zap",
    description: "Zählerstände und Schaltzustände",
  },
  { key: "site-plan", label: "Platzplan", href: "/platzplan", icon: "Map", description: "Karte & Belegung" },
  { key: "planner", label: "Planer", href: "/planner", icon: "CalendarDays", description: "Freie Zeiträume je Platz" },
  { key: "guests", label: "Gäste", href: "/guests", icon: "Users" },
  { key: "mail", label: "E-Mail", href: "/mail", icon: "Mail", description: "Posteingang, E-Mail-Vorlagen und Rundmails" },
  {
    key: "bookings",
    label: "Buchungen",
    href: "/bookings?status=requested",
    icon: "CalendarDays",
    badgeKey: "inquiriesOpen",
    description: "Reservierungen & offene Anfragen",
  },
  {
    key: "pos",
    label: "Schnellverkauf",
    href: "/pos",
    icon: "ShoppingCart",
    description: "Barverkauf ohne Buchung",
  },
  { key: "contracts", label: "Verträge", href: "/contracts", icon: "ClipboardList" },
  {
    key: "tickets",
    label: "Tickets",
    href: "/tickets",
    icon: "LifeBuoy",
    badgeKey: "ticketsOpen",
    description: "Anliegen melden; Camping-Admin bearbeitet sie",
  },
  { key: "visitors", label: "Besucher", href: "/besucher", icon: "Users", description: "Besucheranmeldungen Dauercamper" },
];

/** „Mehr“-Menü – logisch gruppiert */
export const MORE_MENU_GROUPS: NavGroup[] = [
  {
    key: "billing",
    label: "Abrechnung",
    items: [
      { key: "invoices", label: "Rechnungen", href: "/invoices", icon: "Receipt" },
      { key: "cashbook", label: "Kassenjournal", href: "/cashbook", icon: "Wallet" },
      {
        key: "daily-closing",
        label: "Tagesabschluss",
        href: "/daily-closing",
        icon: "ClipboardCheck",
      },
    ],
  },
  {
    key: "admin",
    label: "Verwaltung",
    items: [
      { key: "stammdaten", label: "Stammdaten", href: "/stammdaten", icon: "Settings" },
      { key: "reports", label: "Auswertungen", href: "/reports", icon: "BarChart3" },
      {
        key: "price-simulation",
        label: "Preisfindung",
        href: "/reports/preisfindung",
        icon: "Tags",
      },
    ],
  },
  {
    key: "system",
    label: "System",
    items: [{ key: "audit", label: "Protokoll", href: "/audit", icon: "Shield" }],
  },
];

/** Alle Navigationsziele für Command Palette & ACL */
export const ALL_NAV_ITEMS: NavItem[] = [
  ...SIDEBAR_PRIMARY,
  ...MORE_MENU_GROUPS.flatMap((g) => g.items),
];

/**
 * Stammdaten-Unternavigation.
 *
 * Labels und Beschreibungen sind bewusst in Alltagssprache gehalten, damit auch
 * neue Rezeptionskräfte ohne Vorwissen erkennen, was sich hinter einem Bereich
 * verbirgt. `keywords` ergänzen die Suche um Begriffe, nach denen im Alltag
 * gesucht wird („Anzahlung“, „Logo“, „Abreisezeit“ …).
 */
export const STAMMDATEN_NAV: NavItem[] = [
  // Platz & Stellplätze
  {
    key: "stammdaten-site-plan",
    label: "Platzplan",
    href: "/stammdaten/platzplan",
    icon: "Map",
    description: "Platzkarte hochladen und Stellplätze auf der Karte einzeichnen",
    keywords: ["Karte", "Lageplan", "Bild", "Zeichnen", "Bereiche"],
  },
  {
    key: "stammdaten-site-plan-editor",
    label: "Platzplan-Editor",
    href: "/stammdaten/platzplan-editor",
    icon: "PenLine",
    description: "Planbild per KI in SVG umwandeln, Flächen bearbeiten, Plätze zuweisen, Symbole setzen",
    keywords: ["SVG", "Vektor", "KI", "Editor", "Symbole", "Bäume", "Stromkasten", "Zeichnen", "Import", "Export"],
  },
  {
    key: "stammdaten-pitches",
    label: "Stellplätze",
    href: "/stammdaten/plaetze",
    icon: "LayoutGrid",
    description: "Alle Stellplätze mit Größe, Strom, Wasser und Eigenschaften",
    keywords: ["Plätze", "Parzelle", "Stellplatz anlegen", "Ausstattung", "Sperren", "Ausblenden", "Eigenschaften"],
  },
  {
    key: "stammdaten-categories",
    label: "Platzkategorien",
    href: "/stammdaten/kategorien",
    icon: "Tags",
    description: "Stellplätze in Kategorien einteilen, z. B. Komfort oder Standard",
    keywords: ["Kategorie", "Farbe", "Komfort", "Standard", "Marketing"],
  },

  // Preise & Verkauf
  {
    key: "stammdaten-articles",
    label: "Artikel & Preise",
    href: "/stammdaten/artikel",
    icon: "Package",
    description: "Übernachtung, Personen, Strom, Shop-Artikel und ihre Preise",
    keywords: ["Preis ändern", "Preisliste", "MwSt", "Mehrwertsteuer", "Leistungen", "Tarife", "Excel", "Kasse", "Shop"],
  },
  {
    key: "stammdaten-rules",
    label: "Buchungszuordnung",
    href: "/stammdaten/zuordnung",
    icon: "BookOpen",
    description: "Welcher Artikel bei einer Buchung automatisch berechnet wird",
    keywords: ["Zuordnung", "Automatische Posten", "Übernachtungspreis", "Kurtaxe", "Besucher", "Regeln"],
    badgeKey: "chargeRulesRequiredOpen",
  },
  {
    key: "stammdaten-discounts",
    label: "Rabatte",
    href: "/stammdaten/rabatte",
    icon: "Percent",
    description: "Ermäßigungen, z. B. für Kinder, Stammgäste oder Aktionen",
    keywords: ["Ermäßigung", "Nachlass", "Prozent", "Gutschein"],
  },
  {
    key: "stammdaten-article-categories",
    label: "Artikelkategorien",
    href: "/stammdaten/artikel-kategorien",
    icon: "FolderTree",
    description: "Gruppen für Artikel in Kasse und Gäste-Shop",
    keywords: ["Warengruppe", "Gruppierung", "Kasse", "Shop"],
  },
  {
    key: "stammdaten-suppliers",
    label: "Lieferanten",
    href: "/stammdaten/lieferanten",
    icon: "Truck",
    description: "Lieferanten für Shop-Artikel und Bestellungen",
    keywords: ["Bestellung", "Lieferung", "Großhändler", "Brötchen"],
  },
  {
    key: "stammdaten-accounts",
    label: "Konten",
    href: "/stammdaten/konten",
    icon: "Landmark",
    description: "Erlöskonten für die Buchhaltung (SKR03 / SKR04)",
    keywords: ["Buchhaltung", "Kontenrahmen", "SKR03", "SKR04", "Steuerberater", "Export", "DATEV"],
  },

  // Buchung & Rechnung
  {
    key: "stammdaten-settings",
    label: "Buchungs- & Zahlungsregeln",
    href: "/stammdaten/einstellungen",
    icon: "SlidersHorizontal",
    description: "Anzahlung, Zahlungsziel, Abreise-Uhrzeit, Gästetypen und Strom-Abrechnung",
    keywords: [
      "Einstellungen",
      "Anzahlung",
      "Zahlungsziel",
      "Zahlungskonditionen",
      "Fälligkeit",
      "Check-out",
      "Abreisezeit",
      "Abreise-Uhrzeit",
      "Überfällig",
      "Gästetypen",
      "Mindestaufenthalt",
      "Strom",
      "kWh",
      "Belegung",
      "Auslastung",
      "Tourist",
      "Vorkasse",
    ],
  },
  {
    key: "stammdaten-online-booking",
    label: "Online-Buchung",
    href: "/stammdaten/online-buchung",
    icon: "Globe",
    description: "Buchungswebsite: ein-/ausschalten, Saisons, Mindestaufenthalt, Buchungsstrecke",
    keywords: ["Website", "Buchungsportal", "Saison", "Anreisearten", "Zusatzartikel", "Upselling", "Bestätigung", "Online"],
  },
  {
    key: "stammdaten-templates",
    label: "PDF-Texte",
    href: "/stammdaten/texte",
    icon: "FileText",
    description: "PDF-Texte für Rechnungen und Buchungsbestätigungen",
    keywords: ["E-Mail", "Vorlage", "Bestätigung", "Platzhalter", "Dokument", "Brief", "Mahnung"],
  },

  // Gäste-Kommunikation & Geräte
  {
    key: "stammdaten-website",
    label: "Website",
    href: "/stammdaten/website",
    icon: "PenLine",
    description: "Fotos, Kacheln und Texte der Website – auch für die Rezeption",
    keywords: ["Website", "Text", "Bild", "Startseite", "Galerie", "Inhalt", "Kachel", "Foto"],
  },
  {
    key: "stammdaten-website-news",
    label: "Website-News",
    href: "/stammdaten/website-news",
    icon: "Megaphone",
    description: "Aktuelle Meldungen und Hinweise auf Ihrer Website",
    keywords: ["Neuigkeiten", "Meldung", "Aktuelles", "Hinweis", "Website"],
  },
  {
    key: "stammdaten-guest-tablet",
    label: "Gäste-Tablet",
    href: "/stammdaten/gaeste-tablet",
    icon: "TabletSmartphone",
    description: "Check-in-Terminal am Empfang: Diashow, Texte und Arbeitsplätze",
    keywords: ["Tablet", "Terminal", "Self-Check-in", "Diashow", "Unterschrift", "Arbeitsplatz"],
  },
  {
    key: "stammdaten-info-screen",
    label: "Infobildschirm",
    href: "/stammdaten/infobildschirm",
    icon: "Monitor",
    description: "Anzeigen im Gelände: Informationsseiten, Veranstaltungen, Geräte",
    keywords: ["Bildschirm", "Display", "Fernseher", "Veranstaltungen", "Wetter", "Seiten"],
  },

  // Betrieb & System
  {
    key: "stammdaten-grundeinstellungen",
    label: "Betriebsdaten",
    href: "/stammdaten/grundeinstellungen",
    icon: "Building2",
    description: "Name, Adresse, Logo, Bankverbindung, Briefkopf und rechtliche Angaben",
    keywords: [
      "Grundeinstellungen",
      "Firma",
      "Adresse",
      "Logo",
      "Briefkopf",
      "IBAN",
      "Bankverbindung",
      "Steuernummer",
      "Rechnungskopf",
      "Impressum",
      "Datenschutz",
      "Verantwortlicher",
    ],
  },
  {
    key: "stammdaten-schnittstellen",
    label: "Schnittstellen",
    href: "/stammdaten/schnittstellen",
    icon: "Plug",
    description: "E-Mail-Versand, Kasse/TSE, EC-Terminal, Stromzähler, Schranke, Kamera",
    keywords: ["Anbindung", "Integration", "SMTP", "TSE", "EC", "Terminal", "Bondrucker", "Schranke", "Kennzeichen", "Fernauslese", "Import"],
  },
];

export interface StammdatenNavGroup {
  key: string;
  label: string;
  /** Kurze Erklärung, was in diesem Bereich eingestellt wird */
  description: string;
  itemKeys: string[];
}

/** Bereiche der Stammdaten – Reihenfolge entspricht dem typischen Einrichtungsablauf */
export const STAMMDATEN_NAV_GROUPS: StammdatenNavGroup[] = [
  {
    key: "site",
    label: "Platz & Stellplätze",
    description: "Wie Ihr Platz aufgebaut ist: Karte, Stellplätze und Kategorien.",
    itemKeys: [
      "stammdaten-site-plan",
      "stammdaten-site-plan-editor",
      "stammdaten-pitches",
      "stammdaten-categories",
    ],
  },
  {
    key: "sales",
    label: "Preise & Verkauf",
    description: "Was Sie anbieten, was es kostet und wie es verbucht wird.",
    itemKeys: [
      "stammdaten-articles",
      "stammdaten-rules",
      "stammdaten-discounts",
      "stammdaten-article-categories",
      "stammdaten-suppliers",
      "stammdaten-accounts",
    ],
  },
  {
    key: "booking",
    label: "Buchung & Rechnung",
    description: "Regeln für Buchungen, Rechnungen, Check-out und die Online-Buchung.",
    itemKeys: ["stammdaten-settings", "stammdaten-online-booking", "stammdaten-templates"],
  },
  {
    key: "guests",
    label: "Gäste-Kommunikation & Geräte",
    description: "Was Gäste sehen: Website-Fotos und Kacheln, Meldungen, Tablet und Infobildschirme.",
    itemKeys: ["stammdaten-website", "stammdaten-website-news", "stammdaten-guest-tablet", "stammdaten-info-screen"],
  },
  {
    key: "system",
    label: "Betrieb & System",
    description: "Daten Ihres Betriebs und technische Anbindungen an andere Systeme.",
    itemKeys: ["stammdaten-grundeinstellungen", "stammdaten-schnittstellen"],
  },
];

export function getStammdatenNavByKey(key: string): NavItem | undefined {
  return STAMMDATEN_NAV.find((item) => item.key === key);
}

/** Bereich (Gruppe), zu dem ein Stammdaten-Eintrag gehört */
export function getStammdatenGroupForItem(itemKey: string): StammdatenNavGroup | undefined {
  return STAMMDATEN_NAV_GROUPS.find((group) => group.itemKeys.includes(itemKey));
}

/** Stammdaten-Eintrag anhand des aktuellen Pfads (ohne Query) */
export function getStammdatenNavByPath(pathname: string): NavItem | undefined {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return STAMMDATEN_NAV.find((item) => {
    const base = item.href.split("?")[0] ?? item.href;
    return normalized === base || normalized.startsWith(`${base}/`);
  });
}

/** Command Palette – alle Bereiche inkl. Stammdaten (stabile Referenz für Hooks) */
export const COMMAND_PALETTE_NAV: NavItem[] = [...ALL_NAV_ITEMS, ...STAMMDATEN_NAV];

/** @deprecated Verwende SIDEBAR_PRIMARY / ALL_NAV_ITEMS */
export const MAIN_NAV: NavItem[] = ALL_NAV_ITEMS;

export function isNavItemActive(pathname: string, href: string): boolean {
  if (href === "#more") return false;
  const pathOnly = href.split("?")[0]?.split("#")[0] ?? href;
  const normalized = pathOnly.replace(/\/+$/, "") || "/";
  if (normalized === "/") return pathname === "/";
  if (normalized === "/stammdaten") return pathname.startsWith("/stammdaten");
  return pathname === normalized || pathname.startsWith(`${normalized}/`);
}

export function flattenNavGroups(groups: NavGroup[]): NavItem[] {
  return groups.flatMap((g) => g.items);
}
